(() => {
  'use strict';
  if (window.NLAOrbitCarousel) return;

  const version = '20261022-uniform-svg-1';
  const mounts = new WeakMap();
  const collectionMounts = new WeakMap();
  const collectionMedia = new Map();

  const pausedCollections = new WeakSet();
  const TAU = Math.PI * 2;
  const finite = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const wrap = (value, count) => count > 0 ? ((value % count) + count) % count : 0;
  const targetForIndex = (position, index, count) => count > 0
    ? index + Math.round((position - index) / count) * count : 0;
  const releaseTarget = (position, velocity, count) => count < 2 ? 0
    : Math.round(position + clamp(velocity * 0.16, -1.15, 1.15));
  function springStep(position, velocity, target, seconds) {
    const dt = clamp(seconds, 0, 0.05);
    const omega = 13;
    const distance = position - target;
    const common = velocity + omega * distance;
    const decay = Math.exp(-omega * dt);
    return {
      position: target + (distance + common * dt) * decay,
      velocity: (velocity - omega * common * dt) * decay
    };
  }
  function visiblePlan(position, count) {
    if (count < 1) return [];
    if (count <= 6) return Array.from({ length: count }, (_, index) => ({
      index, virtualIndex: targetForIndex(position, index, count)
    }));
    const centre = Math.round(position);
    return Array.from({ length: 7 }, (_, slot) => {
      const virtualIndex = centre + slot - 3;
      return { index: wrap(virtualIndex, count), virtualIndex };
    });
  }
  function project(relative, count, width, height) {
    const slots = Math.min(6, Math.max(1, count));
    const angle = relative * TAU / slots;
    const depth = (Math.cos(angle) + 1) / 2;
    const seam = count > 6 ? clamp((3.5 - Math.abs(relative)) / 0.65, 0, 1) : 1;
    return {
      x: Math.sin(angle) * width * (width < 600 ? 0.22 : 0.18) + (count === 2 ? (1 - depth) * width * 0.16 : 0),
      y: (depth - 0.5) * height * 0.115,
      scale: 0.72 + depth * 0.28,
      yaw: -Math.sin(angle) * 38,
      rotation: -Math.sin(angle) * 4.2,
      opacity: (0.38 + depth * 0.62) * seam,
      depth, z: Math.round(depth * 1000)
    };
  }
  const wideSlots = width => width >= 1100 ? 7 : width >= 640 ? 5 : 3;
  function widePlan(position,count,width){
    if(count<1)return [];
    const budget=Math.min(count,wideSlots(width)+2);
    if(count<=budget)return Array.from({length:count},(_,index)=>({index,virtualIndex:targetForIndex(position,index,count)}));
    const centre=Math.round(position);
    return Array.from({length:budget},(_,slot)=>{const virtualIndex=centre+slot-Math.floor(budget/2);return {index:wrap(virtualIndex,count),virtualIndex};});
  }
  function wideProject(relative,count,width,height){
    const slots=Math.min(wideSlots(width),Math.max(1,count));
    const distance=Math.abs(relative),edge=slots/2+.5;
    const depth=clamp(1-distance/edge,0,1);
    return {x:relative*width/(slots+.2),y:Math.pow(Math.min(distance,edge),1.25)*5,
      scale:1-Math.min(distance*.045,.18),yaw:-clamp(relative*4,-16,16),rotation:-relative*.55,
      opacity:(.72+depth*.28)*clamp((edge-distance)/.75,0,1),depth,z:Math.round(depth*1000)};
  }
  const math = Object.freeze({ wrap, targetForIndex, releaseTarget, springStep, visiblePlan, project, wideSlots, widePlan, wideProject });

  function mount(host, supplied = {}) {
    if (!host || !host.ownerDocument) return Object.assign(() => {}, { getState: () => ({ phase: 'unmounted', count: 0 }) });
    mounts.get(host)?.();
    const doc = host.ownerDocument;
    const view = doc.defaultView || window;
    // Apple touch uses Touch Events so Safari scroll arbitration cannot strand a pointer.
    // Mouse, pen and Android keep their existing Pointer Event path.
    const appleTouch = 'ontouchstart' in view && (/iP(?:hone|ad|od)/.test(view.navigator?.userAgent || '') ||
      (view.navigator?.platform === 'MacIntel' && view.navigator?.maxTouchPoints > 1));
    const acceptsPointer = event => !(appleTouch && event.pointerType === 'touch');
    const touchPoint = (touch, event, ending = false) => ({
      pointerId: 'touch:' + touch.identifier, pointerType: 'touch', isPrimary: true, button: 0,
      clientX: touch.clientX, clientY: touch.clientY,
      cancelable: !ending && event.cancelable,
      preventDefault: () => { if (!ending && event.cancelable) event.preventDefault(); }
    });

    host.dataset.swipeInput = appleTouch ? 'apple-touch' : 'pointer';
    const abort = new view.AbortController();
    const listen = (node, name, callback, options = {}) => node.addEventListener(name, callback, { ...options, signal: abort.signal });
    const safeURL = (value, image = false) => {
      const raw = typeof value === 'string' ? value : image ? value?.url || value?.src : '';
      if (!raw) return '';
      try {
        const url = new URL(raw, doc.baseURI);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
      } catch { return ''; }
    };
    const seen = new Set();
    const items = (Array.isArray(supplied.items) ? supplied.items : []).filter(item => {
      const id = String(item?.id ?? '');
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    }).map(item => ({
      id: String(item.id), title: String(item.title || item.name || 'Piece'),
      image: safeURL(item.image, true), href: safeURL(item.href),
      price: item.price == null ? '' : String(item.price), original: item
    }));
    const count = items.length;
    const make = (tag, className, text) => {
      const element = doc.createElement(tag);
      if (className) element.className = className;
      if (text != null) element.textContent = text;
      return element;
    };
    const wide = supplied.layout === 'wide';
    const uniform = supplied.uniformSize === true;
    const root = make('div', 'nla-product-orbit' + (wide ? ' nla-product-orbit--wide' : '') + (uniform ? ' nla-product-orbit--uniform' : ''));
    root.dataset.orbitVersion = version;
    const stage = make('div', 'nla-product-orbit__stage');
    stage.tabIndex = 0;
    stage.setAttribute('role', 'region');
    stage.setAttribute('aria-roledescription', 'Karussell');
    stage.setAttribute('aria-label', String(supplied.label || 'Pieces entdecken'));
    stage.setAttribute('aria-keyshortcuts', 'ArrowLeft ArrowRight Home End Enter');
    const grid = make('div', 'nla-product-orbit__grid');
    const ellipse = make('div', 'nla-product-orbit__ellipse');
    const guides = make('div', 'nla-product-orbit__guides');
    for (const decoration of [grid, ellipse, guides]) decoration.setAttribute('aria-hidden', 'true');
    for (const corner of ['tl', 'tr', 'bl', 'br']) guides.append(make('i', `nla-product-orbit__corner nla-product-orbit__corner--${corner}`));
    const itemLayer = make('div', 'nla-product-orbit__items');
    const hint = make('p', 'nla-product-orbit__hint', 'Ziehen oder wischen · mit den Pfeilen wechseln');
    stage.append(grid, ellipse, guides, itemLayer);
    const caption = make('div', 'nla-product-orbit__caption');
    const heading = make('h3', 'nla-product-orbit__title');
    const price = make('p', 'nla-product-orbit__price');
    const controls = make('div', 'nla-product-orbit__controls');
    const previous = make('button', 'nla-product-orbit__arrow', '←');
    const next = make('button', 'nla-product-orbit__arrow', '→');
    previous.type = next.type = 'button';
    previous.setAttribute('aria-label', 'Vorheriges Piece');
    next.setAttribute('aria-label', 'Nächstes Piece');
    previous.disabled = next.disabled = count < 2;
    const counter = make('span', 'nla-product-orbit__counter');
    counter.setAttribute('aria-live', 'polite');
    counter.setAttribute('aria-atomic', 'true');
    const discover = make('a', 'nla-product-orbit__discover', 'Piece entdecken ↗');
    controls.append(previous, counter, next);
    caption.append(heading, price, controls, discover, hint);
    root.append(stage, caption);
    host.append(root);

    const media = view.matchMedia('(prefers-reduced-motion: reduce)');
    const records = new Map();
    let destroyed = false;
    let position = count ? wrap(Math.round(finite(supplied.initialIndex)), count) : 0;
    let velocity = 0;
    let target = position;
    let phase = count ? 'idle' : 'empty';
    let frame = 0;
    let paintFrame = 0;
    let width = 640;
    let height = 420;
    let gesture = null;
    let lastFrameTime = 0;
    let suppressedUntil = 0;
    let selectedIndex = -2;
    let notifiedIndex = -1;
    const clock = () => view.performance.now();
    const activeIndex = () => count ? wrap(Math.round(position), count) : -1;
    const state = () => ({
      version, phase, count, position, velocity, target,
      index: activeIndex(), productId: items[activeIndex()]?.id || '',
      visibleCount: records.size, reducedMotion: media.matches
    });
    function syncAttributes() {
      const current = state();
      host.dataset.orbitMounted = String(!destroyed);
      host.dataset.orbitVersion = version;
      host.dataset.orbitPhase = phase;
      host.dataset.orbitPosition = position.toFixed(6);
      host.dataset.orbitVelocity = velocity.toFixed(6);
      host.dataset.orbitTarget = target.toFixed(6);
      host.dataset.orbitCount = String(count);
      host.dataset.orbitIndex = String(current.index);
      host.dataset.orbitProductId = current.productId;
      host.dataset.orbitVisibleCount = String(records.size);
      host.dataset.orbitReducedMotion = String(media.matches);
    }
    function createRecord(index) {
      const item = items[index];
      const anchor = make('a', 'nla-product-orbit__piece');
      if (item.href) anchor.href = item.href;
      else anchor.setAttribute('role', 'button');
      anchor.dataset.orbitItem = item.id;
      anchor.dataset.orbitItemIndex = String(index);
      anchor.setAttribute('aria-label', item.title);
      anchor.draggable = false;
      const shadow = make('span', 'nla-product-orbit__shadow');
      shadow.setAttribute('aria-hidden', 'true');
      const missing = make('span', 'nla-product-orbit__missing', item.title);
      missing.hidden = true;
      const image = make('img', 'nla-product-orbit__image');
      if (item.original?.alpha === true) image.dataset.nlaAlpha = 'verified';
      image.alt = item.title;
      image.draggable = false;
      image.decoding = 'async';
      image.loading = supplied.normalizeAlpha === true && Math.abs(index - finite(supplied.initialIndex)) > 2 ? 'lazy' : 'eager';
      const broken = () => {
        image.hidden = true;
        missing.hidden = false;
        anchor.classList.add('is-image-unavailable');
        anchor.dataset.orbitImageError = 'true';
      };
      image.addEventListener('error', broken, { once: true });
      if (item.image) {
        try {
          const original = new URL(item.image, doc.baseURI);
          const shopImage = ['nla-shop.ch', 'www.nla-shop.ch', 'h66yzn-x1.myshopify.com'].includes(original.hostname) && original.pathname.startsWith('/cdn/shop/files/');
          const cdnImage = original.hostname === 'cdn.shopify.com' && original.pathname.startsWith('/s/files/1/1085/7670/2805/files/');
          if (shopImage || cdnImage) {
            image.sizes = supplied.normalizeAlpha === true ? '(min-width: 768px) 60vw, 95vw' : wide ? '(min-width: 1100px) 17vw, (min-width: 640px) 24vw, 40vw' : '40vw';
            image.srcset = [240, 360, 540, 720, 1080, 1400].map(width => {
              const url = new URL(original); url.searchParams.set('width', String(width));
              return url.href + ' ' + width + 'w';
            }).join(', ');
          }
        } catch { /* Keep original source if a responsive URL cannot be formed. */ }
        image.src = item.image;
      }
      else broken();
      const crop = supplied.normalizeAlpha === true ? item.original.crop : null;
      let normalized = null;
      if (crop && crop.width > 0 && crop.height > 0 && crop.box?.length === 4) {
        const ns = 'http://www.w3.org/2000/svg';
        normalized = doc.createElementNS(ns, 'svg');
        normalized.setAttribute('viewBox', crop.box.join(' '));
        normalized.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        normalized.setAttribute('aria-hidden', 'true');
        normalized.classList.add('nla-orbit-normalized');
        const artwork = doc.createElementNS(ns, 'image');
        artwork.setAttribute('width', String(crop.width));
        artwork.setAttribute('height', String(crop.height));
        normalized.append(artwork);
        image.addEventListener('load', () => artwork.setAttribute('href', image.currentSrc || item.image));
        image.addEventListener('error', () => normalized.setAttribute('hidden', ''));
        image.classList.add('nla-orbit-source-image');
      }
      anchor.append(shadow, image, ...(normalized ? [normalized] : []), missing);
      itemLayer.append(anchor);
      return { anchor, image, index, broken };
    }
    function paint() {
      if (destroyed) return;
      const plan = wide ? widePlan(position, count, width) : visiblePlan(position, count);
      if(wide)root.style.setProperty("--orbit-wide-piece",(100/(Math.min(wideSlots(width),Math.max(1,count))+.2)*1.15).toFixed(3)+"%");
      const visible = new Set(plan.map(entry => entry.index));
      for (const [index, record] of records) {
        if (!visible.has(index)) {
          if (record.anchor.contains(doc.activeElement)) stage.focus({ preventScroll: true });
          record.image.removeAttribute('srcset');
        record.image.removeAttribute('src');
          record.image.removeEventListener('error', record.broken);
          record.anchor.remove();
          records.delete(index);
        }
      }
      const active = activeIndex();
      for (const entry of plan) {
        let record = records.get(entry.index);
        if (!record) { record = createRecord(entry.index); records.set(entry.index, record); }
        const relative = entry.virtualIndex - position;
        const point = wide ? wideProject(relative, count, width, height) : project(relative, count, width, height);
        if (supplied.fullWidth === true && !wide) { point.x *= width < 600 ? 1.15 : 1.65; point.yaw *= 0.35; point.rotation *= 0.4; point.opacity = point.opacity > 0 ? 1 : 0; }
        if (uniform) { point.scale = 1; point.yaw = 0; point.rotation = 0; }
        const selected = entry.index === active;
        record.anchor.style.transform = `translate(-50%,-50%) translate(${point.x.toFixed(3)}px,${point.y.toFixed(3)}px) perspective(1100px) rotateY(${point.yaw.toFixed(3)}deg) rotateZ(${point.rotation.toFixed(3)}deg) scale(${point.scale.toFixed(5)})`;
        record.anchor.style.opacity = point.opacity.toFixed(4);
        record.anchor.style.zIndex = String(point.z);
        record.anchor.style.pointerEvents = point.opacity < 0.08 ? 'none' : '';
        record.anchor.dataset.orbitRelative = relative.toFixed(6);
        record.anchor.dataset.orbitDepth = point.depth.toFixed(6);
        record.anchor.dataset.orbitYaw = point.yaw.toFixed(6);
        record.anchor.dataset.orbitScale = point.scale.toFixed(6);
        record.anchor.dataset.orbitVirtualIndex = String(entry.virtualIndex);
        record.anchor.dataset.orbitActive = String(selected);
        record.anchor.classList.toggle('is-active', selected);
        record.anchor.tabIndex = selected ? 0 : -1;
        if (selected) record.anchor.setAttribute('aria-current', 'true');
        else record.anchor.removeAttribute('aria-current');
      }
      if (active !== selectedIndex) {
        selectedIndex = active;
        const item = items[active];
        heading.textContent = item?.title || 'Neue Pieces werden vorbereitet.';
        price.textContent = item?.price || '';
        price.hidden = !item?.price;
        counter.textContent = item ? `${String(active + 1).padStart(2, '0')} / ${String(count).padStart(2, '0')}` : '0 / 0';
        if (item?.href) discover.href = item.href;
        else discover.removeAttribute('href');
        discover.hidden = !item || (!item.href && typeof supplied.open !== 'function');
      }
      root.classList.toggle('is-dragging', phase === 'dragging');
      root.classList.toggle('is-empty', !count);
      syncAttributes();
    }
    function requestPaint() {
      if (!paintFrame && !destroyed) paintFrame = view.requestAnimationFrame(() => { paintFrame = 0; paint(); });
    }
    function cancelSpring() {
      if (frame) view.cancelAnimationFrame(frame);
      frame = 0;
      lastFrameTime = 0;
    }
    function notifySelection() {
      const index = activeIndex();
      if (index < 0 || index === notifiedIndex || destroyed) return;
      notifiedIndex = index;
      if (typeof supplied.onSelect === 'function') supplied.onSelect(items[index].original, index);
    }
    function spring(time) {
      frame = 0;
      if (destroyed) return;
      if (!lastFrameTime) lastFrameTime = time;
      const nextState = springStep(position, velocity, target, (time - lastFrameTime) / 1000);
      lastFrameTime = time;
      position = nextState.position;
      velocity = nextState.velocity;
      if (Math.abs(position - target) < 0.0008 && Math.abs(velocity) < 0.008) {
        position = target;
        velocity = 0;
        phase = count ? 'idle' : 'empty';
        paint();
        notifySelection();
        return;
      }
      paint();
      frame = view.requestAnimationFrame(spring);
    }
    function settle(nextTarget, initialVelocity = 0) {
      cancelSpring();
      target = count < 2 ? 0 : finite(nextTarget, Math.round(position));
      velocity = finite(initialVelocity);
      if (media.matches || doc.hidden || count < 2 || (Math.abs(position - target) < 0.00001 && Math.abs(velocity) < 0.00001)) {
        position = target;
        velocity = 0;
        phase = count ? 'idle' : 'empty';
        paint();
        notifySelection();
        return;
      }
      phase = 'settling';
      paint();
      frame = view.requestAnimationFrame(spring);
    }
    function step(direction) {
      if (destroyed || count < 2) return;
      const base = phase === 'settling' ? target : Math.round(position);
      settle(base + direction, velocity);
    }
    function select(index) {
      if (destroyed || !count) return;
      const normalized = wrap(Math.round(finite(index)), count);
      settle(targetForIndex(position, normalized, count), velocity);
    }
    function openItem(item, event) {
      if (!item || typeof supplied.open !== 'function') return false;
      supplied.open(item.original, event);
      return true;
    }
    function finishGesture(cancelled = false) {
      if (!gesture) return;
      const ended = gesture;
      gesture = null;
      if (!ended.touch && stage.hasPointerCapture?.(ended.id)) {
        try { stage.releasePointerCapture(ended.id); } catch { /* Capture can end before cancellation. */ }
      }
      if (ended.active) {
        suppressedUntil = clock() + 360;
        const staleVelocity = clock() - ended.lastTime > 100 ? 0 : velocity;
        settle(cancelled ? (ended.touch ? ended.resumeTarget : Math.round(position)) : releaseTarget(position, staleVelocity, count), cancelled ? 0 : staleVelocity);
      } else if (phase === 'holding') settle(ended.resumeTarget, velocity);
    }
    function measure() {
      if (destroyed) return;
      const box = stage.getBoundingClientRect();
      if (gesture?.active && box.width && box.width !== width) {
        gesture.x = gesture.lastClientX;
        gesture.y = gesture.lastClientY;
        gesture.position = position;
        gesture.lastPosition = position;
        gesture.lastTime = clock();
      }
      width = box.width || host.clientWidth || width;
      height = box.height || height;
      requestPaint();
    }
    // NLA_IOS_INPUT_START
    function beginPointer(event) {
      if (!acceptsPointer(event) && typeof event.pointerId !== 'string') return;
      if (destroyed || count < 2 || gesture || event.isPrimary === false || event.button !== 0) return;
      const resumeTarget = phase === 'settling' ? target : Math.round(position);
      cancelSpring();
      gesture = { id: event.pointerId, touch: appleTouch && typeof event.pointerId === 'string', x: event.clientX, y: event.clientY, lastClientX: event.clientX, lastClientY: event.clientY, position, active: false, lastPosition: position, lastTime: clock(), resumeTarget };
      phase = 'holding';
      syncAttributes();
    }
    function movePointer(event) {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      gesture.lastClientX = event.clientX;
      gesture.lastClientY = event.clientY;
      if (!gesture.active) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx) * 1.1) { finishGesture(true); return; }
        if (Math.abs(dx) < (gesture.touch ? 6 : 8) || Math.abs(dx) < Math.abs(dy) * (gesture.touch ? 1.05 : 1.1)) return;
        gesture.active = true;
        if (!gesture.touch) { try { stage.setPointerCapture?.(gesture.id); } catch { /* Capture may already have ended. */ } }
      }
      if (event.cancelable) event.preventDefault();
      const time = clock();
      const nextPosition = gesture.position - dx / (wide ? Math.max(70,width/(wideSlots(width)+.2)) : Math.max(90,width*.235));
      const seconds = Math.max(0.008, (time - gesture.lastTime) / 1000);
      const sample = clamp((nextPosition - gesture.lastPosition) / seconds, -12, 12);
      velocity = velocity * 0.3 + sample * 0.7;
      position = nextPosition;
      gesture.lastPosition = position;
      gesture.lastTime = time;
      phase = 'dragging';
      requestPaint();
    }
    listen(stage, 'pointerdown', event => { if (acceptsPointer(event)) beginPointer(event); });
    listen(stage, 'pointermove', event => { if (acceptsPointer(event)) movePointer(event); }, { passive: false });
    listen(view, 'pointerup', event => { if (acceptsPointer(event) && event.pointerId === gesture?.id) finishGesture(); }, { capture: true });
    listen(view, 'pointercancel', event => { if (acceptsPointer(event) && event.pointerId === gesture?.id) finishGesture(true); }, { capture: true });
    listen(stage, 'lostpointercapture', event => { if (!gesture?.touch && event.target === stage && event.pointerId === gesture?.id) finishGesture(true); });
    listen(view, 'blur', () => finishGesture(true));
    listen(view, 'pagehide', () => finishGesture(true));
    listen(doc, 'visibilitychange', () => { if (doc.hidden) finishGesture(true); });
    if (appleTouch) {
      listen(stage, 'touchstart', event => {
        if (event.touches.length !== 1) { finishGesture(true); return; }
        if (gesture) finishGesture(true);
        beginPointer(touchPoint(event.touches[0], event));
      }, { passive: true });
      listen(stage, 'touchmove', event => {
        if (!gesture?.touch) return;
        if (event.touches.length !== 1) { finishGesture(true); return; }
        const point = event.touches[0];
        if ('touch:' + point.identifier === gesture.id) movePointer(touchPoint(point, event));
      }, { passive: false });
      listen(view, 'touchend', event => {
        if (!gesture?.touch) return;
        const point = Array.from(event.changedTouches).find(touch => 'touch:' + touch.identifier === gesture.id);
        if (!point) return;
        // Include final coordinates even if Safari coalesced the last touchmove.
        movePointer(touchPoint(point, event, true));
        finishGesture();
      }, { passive: true, capture: true });
      listen(view, 'touchcancel', () => { if (gesture?.touch) finishGesture(true); }, { passive: true, capture: true });
    }
    // NLA_IOS_INPUT_END
    listen(stage, 'dragstart', event => event.preventDefault());
    listen(stage, 'click', event => {
      if (clock() < suppressedUntil) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      const anchor = event.target.closest?.('[data-orbit-item]');
      if (!anchor || !stage.contains(anchor) || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const index = Number(anchor.dataset.orbitItemIndex);
      if (index !== activeIndex()) {
        event.preventDefault();
        settle(Number(anchor.dataset.orbitVirtualIndex), 0);
      } else if (typeof supplied.open === 'function') {
        event.preventDefault();
        openItem(items[index], event);
      }
    }, { capture: true });
    listen(discover, 'click', event => {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (typeof supplied.open === 'function') { event.preventDefault(); openItem(items[activeIndex()], event); }
    });
    listen(previous, 'click', () => step(-1));
    listen(next, 'click', () => step(1));
    listen(stage, 'keydown', event => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.defaultPrevented) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        if (event.target !== stage) stage.focus({ preventScroll: true });
        step(event.key === 'ArrowLeft' ? -1 : 1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        if (event.target !== stage) stage.focus({ preventScroll: true });
        select(event.key === 'Home' ? 0 : count - 1);
      } else if (event.key === 'Enter' && event.target === stage) {
        event.preventDefault();
        if (typeof supplied.open === 'function') openItem(items[activeIndex()], event);
        else if (items[activeIndex()]?.href) discover.click();
      }
    });
    listen(media, 'change', () => { if (media.matches) { finishGesture(true); settle(Math.round(position), 0); } else paint(); });
    listen(doc, 'visibilitychange', () => { if (doc.hidden) { finishGesture(true); settle(Math.round(position), 0); } });
    let resize = null;
    if (view.ResizeObserver) { resize = new view.ResizeObserver(measure); resize.observe(stage); }
    else listen(view, 'resize', measure, { passive: true });

    const dispose = Object.assign(() => {
      if (destroyed) return;
      destroyed = true;
      if (gesture && !gesture.touch && stage.hasPointerCapture?.(gesture.id)) {
        try { stage.releasePointerCapture(gesture.id); } catch { /* No current capture. */ }
      }
      gesture = null;
      cancelSpring();
      if (paintFrame) view.cancelAnimationFrame(paintFrame);
      paintFrame = 0;
      resize?.disconnect();
      abort.abort();
      for (const record of records.values()) {
        record.image.removeAttribute('srcset');
        record.image.removeAttribute('src');
        record.image.removeEventListener('error', record.broken);
      }
      records.clear();
      root.remove();
      phase = 'destroyed';
      syncAttributes();
      if (mounts.get(host) === dispose) mounts.delete(host);
    }, { getState: state, next: () => step(1), previous: () => step(-1), select });
    mounts.set(host, dispose);
    if (!count) {
      heading.textContent = 'Neue Pieces werden vorbereitet.';
      discover.hidden = true;
      hint.hidden = true;
    }
    measure();
    paint();
    notifySelection();
    return dispose;
  }

  function collectionWrappers(scope) {
    const found = [];
    if (scope?.matches?.('[data-nla-product-orbit-collection]')) found.push(scope);
    if (scope?.querySelectorAll) found.push(...scope.querySelectorAll('[data-nla-product-orbit-collection]'));
    return found;
  }
  function stageCollectionItems(items, media, sourceURL) {
    return items.flatMap(item => {
      const entry = media?.[String(item.id)];
      const source = entry?.asset || entry?.url;
      if (entry?.alpha !== true || typeof source !== 'string' || !source.trim()) return [];
      let image;
      try {
        const parsed = new URL(source, sourceURL);
        if (parsed.protocol !== 'https:' || !['nla-shop.ch', 'cdn.shopify.com', 'h66yzn-x1.myshopify.com'].includes(parsed.hostname)) return [];
        image = parsed.href;
      } catch { return []; }
      return [{ ...item, image, crop: entry.crop }];
    });
  }
  function bootCollections(scope = window.document) {
    for (const wrapper of collectionWrappers(scope)) {
      if (pausedCollections.has(wrapper)) continue;
      const host = wrapper.querySelector('[data-nla-product-orbit-mount]');
      const source = wrapper.querySelector('script[data-nla-product-orbit-items]');
      const previous = collectionMounts.get(wrapper);
      const jsonText = source?.textContent || '';
      if (previous && previous.host === host && previous.jsonText === jsonText && host?.querySelector('.nla-product-orbit') && previous.dispose.getState().phase !== 'destroyed') continue;
      previous?.dispose();
      collectionMounts.delete(wrapper);
      wrapper.dataset.nlaOrbitCollectionMounted = 'false';
      if (!host || !source || mounts.has(host)) continue;
      let items;
      try { items = JSON.parse(jsonText); } catch { continue; }
      if (!Array.isArray(items)) continue;
      const mediaSource = wrapper.dataset.nlaOrbitStageMedia || '';
      let mediaURL = '';
      try { if (mediaSource) mediaURL = new URL(mediaSource, wrapper.ownerDocument.baseURI).href; } catch { /* Keep native images for an invalid source. */ }
      let normalizeAlpha = false;
      if (mediaURL) {
        let record = collectionMedia.get(mediaURL);
        if (!record) {
          record = { status: 'pending', data: null };
          collectionMedia.set(mediaURL, record);
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 8000);
          record.promise = fetch(mediaURL, { signal: controller.signal })
            .then(response => { if (!response.ok) throw new Error('Stage media unavailable'); return response.json(); })
            .then(data => { record.data = data; record.status = 'ready'; },
              () => { record.status = 'failed'; })
            .finally(() => clearTimeout(timer));
        }
        if (record.status === 'pending') {
          if (wrapper.dataset.nlaOrbitMediaPending !== mediaURL) {
            wrapper.dataset.nlaOrbitMediaPending = mediaURL;
            record.promise.then(() => {
              delete wrapper.dataset.nlaOrbitMediaPending;
              if (wrapper.isConnected && !pausedCollections.has(wrapper)) bootCollections(wrapper);
            });
          }
          continue;
        }
        if (record.status === 'ready') {
          items = stageCollectionItems(items, record.data, mediaURL);
          normalizeAlpha = true;
        }
      }
      // A missing or failed alpha manifest must never expose opaque native images.
      if (!normalizeAlpha) items = [];
      const dispose = mount(host, { items, label: wrapper.dataset.nlaOrbitLabel || 'NLA Editions', uniformSize: wrapper.dataset.nlaOrbitLabel === 'NLA Editions – Kleidung', normalizeAlpha });
      collectionMounts.set(wrapper, { dispose, host, jsonText });
      wrapper.dataset.nlaOrbitCollectionMounted = 'true';
    }
  }
  function unloadCollections(scope) {
    for (const wrapper of collectionWrappers(scope)) {
      pausedCollections.add(wrapper);
      collectionMounts.get(wrapper)?.dispose();
      collectionMounts.delete(wrapper);
      wrapper.dataset.nlaOrbitCollectionMounted = 'false';
    }
  }
  window.NLAOrbitCarousel = Object.freeze({ version, mount, math, bootCollections });
  if (window.customElements && window.HTMLElement && !window.customElements.get('nla-product-orbit-collection')) {
    class NLAOrbitCollection extends window.HTMLElement {
      connectedCallback() {
        const connect = () => {
          if (!this.isConnected) return;
          pausedCollections.delete(this);
          if (!this._orbitObserver && window.MutationObserver) {
            this._orbitObserver = new window.MutationObserver(() => bootCollections(this));
            this._orbitObserver.observe(this, { childList: true, characterData: true, subtree: true });
          }
          bootCollections(this);
        };
        if (window.queueMicrotask) window.queueMicrotask(connect);
        else Promise.resolve().then(connect);
      }
      disconnectedCallback() {
        this._orbitObserver?.disconnect();
        this._orbitObserver = null;
        unloadCollections(this);
      }
    }
    window.customElements.define('nla-product-orbit-collection', NLAOrbitCollection);
  }
  if (window.document) {
    const doc = window.document;
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => bootCollections(doc), { once: true });
    else bootCollections(doc);
    doc.addEventListener('shopify:section:load', event => {
      for (const wrapper of collectionWrappers(event.target)) pausedCollections.delete(wrapper);
      bootCollections(event.target);
    });
    doc.addEventListener('shopify:section:unload', event => unloadCollections(event.target));
  }
})();


